import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Autocomplete } from '@/modules/common';
import { OptionType } from '@/modules/common/types';
import { Mask } from '@/modules/masks/types';
import { OverlayItem } from '@/modules/overlays/types';

import { addEventMaskSchema } from '../schema';

interface IProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  masksData: Mask[];
  overlayData: OverlayItem[];
  selectedMask: any;
  onAdd: (mask: any) => void;
  onEdit: (mask: any) => void;
}

export type AddEventMaskValues = z.infer<typeof addEventMaskSchema>;

const defaultMask = {
  maskId: '',
  overlayId: '',
};

export function AddEditMaskDialog({
  isOpen,
  onOpenChange,
  masksData,
  overlayData,
  selectedMask,
  onEdit,
  onAdd,
}: IProps) {
  const form = useForm<AddEventMaskValues>({
    resolver: zodResolver(addEventMaskSchema),
    defaultValues: defaultMask,
  });

  const isEdit = selectedMask !== null;

  const { control, handleSubmit, reset, watch, formState: { isDirty } } = form;

  const [maskId, overlayId] = watch(['maskId', 'overlayId']);

  const maskOptions = masksData?.map((style) => ({
    value: style.name,
    id: style.id,
  }));

  const overlaysOptions = overlayData?.map((item) => ({
    value: item.name,
    id: item.id,
  }));

  const selectedMaskOption = masksData?.find((option) => option.id === maskId);
  const selectedOverlayOption = overlayData?.find(
    (option) => option.id === overlayId
  );

  const handleClose = async (open: boolean) => {
    onOpenChange(open);
    reset(defaultMask);
  };

  const handleFormSubmit = async (values: AddEventMaskValues) => {
    if (isEdit) {
      onEdit(values);
    } else {
      onAdd(values);
    }
    handleClose(false);
  };

  useEffect(() => {
    if (isEdit) {
      form.reset(selectedMask);
    }
  }, [isEdit]);

  return (
    <>
      <Dialog onOpenChange={handleClose} open={isOpen} modal={false}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Mask Details' : 'Add Mask'}</DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSubmit(handleFormSubmit)(event);
              }}
            >
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
                  <Autocomplete
                    label="Mask"
                    name={'maskId'}
                    control={control}
                    searchable
                    placeholder="Select mask"
                    options={maskOptions}
                  />
                  {selectedMaskOption && (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm">Preview</p>
                      <div className="flex gap-1">
                        <img
                          src={selectedMaskOption.imagesPublicUrls.large}
                          alt="Mask preview image"
                          className="w-[40px] h-[40px] rounded-lg"
                        />
                        <img
                          src={selectedMaskOption.imagesPublicUrls.small}
                          alt="Mask preview image"
                          className="w-[40px] h-[40px] rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
                  <Autocomplete
                    label="Overlay"
                    name={'overlayId'}
                    control={control}
                    searchable
                    placeholder="Select overlay"
                    options={overlaysOptions}
                  />
                  {selectedOverlayOption && (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm">Preview</p>
                      <img
                        src={selectedOverlayOption.imagePublicUrl}
                        alt="Overlay preview image"
                        className="w-[40px] h-[40px] rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  data-testid="save-button"
                  // isLoading={sendSmsMutation.isPending}
                  disabled={!isDirty}
                >
                  {isEdit ? 'Save' : 'Add Mask'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
